import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { Reveal, Overline } from "@/components/site/Reveal";
import { useLanguage } from "@/context/LanguageContext";
import { IMAGES } from "@/i18n/assets";

function GalleryTile({ src, caption, className, index, onOpen }) {
  const [pos, setPos] = useState({ x: "50%", y: "50%" });
  return (
    <motion.div
      data-testid={`gallery-tile-${index}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay: index * 0.08, ease: [0.16, 1, 0.3, 1] }}
      onClick={onOpen}
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        setPos({ x: `${((e.clientX - r.left) / r.width) * 100}%`, y: `${((e.clientY - r.top) / r.height) * 100}%` });
      }}
      className={`group relative cursor-pointer overflow-hidden rounded-lg border border-clan-ink/10 ${className}`}
    >
      <div
        className="h-full w-full bg-cover bg-center transition-transform duration-[900ms] ease-out group-hover:scale-105"
        style={{ backgroundImage: `url(${src})` }}
      />
      <div className="absolute inset-0 bg-clan-bg/30 transition-opacity duration-500 group-hover:opacity-0" />
      <div
        className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        style={{ background: `radial-gradient(340px circle at ${pos.x} ${pos.y}, hsl(var(--accent-hsl) / 0.22), transparent 70%)` }}
      />
      <div className="absolute inset-x-0 bottom-0 flex items-end bg-gradient-to-t from-clan-bg/85 to-transparent p-5">
        <span className="translate-y-2 font-mono text-xs uppercase tracking-[0.2em] text-clan-ink opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
          {caption}
        </span>
      </div>
    </motion.div>
  );
}

export function Gallery() {
  const { t } = useLanguage();
  const tiles = [IMAGES.foggyCastle, IMAGES.cliffs, IMAGES.glencoeHouse, IMAGES.irishCoast, IMAGES.bridgeCastle];
  const [active, setActive] = useState(null);

  return (
    <section id="legacy" data-testid="gallery-section" className="relative bg-clan-bg py-28 lg:py-40">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="mb-16 flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <Reveal>
              <Overline>{t.gallery.overline}</Overline>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-serif text-4xl font-light leading-[1.05] tracking-tight text-clan-ink sm:text-5xl lg:text-6xl pearl">
                {t.gallery.title}
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <p className="max-w-xs text-base leading-relaxed text-clan-muted">{t.gallery.subtitle}</p>
          </Reveal>
        </div>

        <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-5 auto-rows-[180px] md:auto-rows-[240px]">
          <GalleryTile src={tiles[0]} caption={t.gallery.captions[0]} index={0} onOpen={() => setActive(0)} className="col-span-2 row-span-2" />
          <GalleryTile src={tiles[1]} caption={t.gallery.captions[1]} index={1} onOpen={() => setActive(1)} className="col-span-2 md:col-span-1 row-span-1" />
          <GalleryTile src={tiles[2]} caption={t.gallery.captions[2]} index={2} onOpen={() => setActive(2)} className="col-span-2 md:col-span-1 row-span-1" />
          <GalleryTile src={tiles[3]} caption={t.gallery.captions[3]} index={3} onOpen={() => setActive(3)} className="col-span-1 md:col-span-1 row-span-1" />
          <GalleryTile src={tiles[4]} caption={t.gallery.captions[4]} index={4} onOpen={() => setActive(4)} className="col-span-1 md:col-span-1 row-span-1" />
        </div>
      </div>

      <AnimatePresence>
        {active !== null && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setActive(null)}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-black/85 p-5 backdrop-blur-md sm:p-8"
          >
            <button
              data-testid="gallery-lightbox-close"
              onClick={() => setActive(null)}
              className="absolute right-6 top-6 grid h-11 w-11 place-items-center rounded-md border border-white/30 text-white transition-colors duration-300 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-clan-accent"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
            <motion.div
              onClick={(e) => e.stopPropagation()}
              initial={{ scale: 0.95, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.97, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="grid max-h-[86vh] w-full max-w-5xl overflow-hidden rounded-lg border border-clan-ink/10 bg-clan-surface md:grid-cols-2"
            >
              <div
                className="min-h-[240px] bg-cover bg-center md:min-h-full"
                style={{ backgroundImage: `url(${tiles[active]})` }}
              />
              <div className="flex flex-col justify-center gap-4 p-8 lg:p-12">
                <span className="font-mono text-xs uppercase tracking-[0.25em] text-clan-accent">{t.gallery.overline}</span>
                <h3 className="font-serif text-3xl text-clan-ink lg:text-4xl">{t.gallery.captions[active]}</h3>
                <p className="text-base leading-relaxed text-clan-muted">{t.galleryStories[active]}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
